'use client'

import Link from 'next/link'
import { useState } from 'react'

import { CopyButton } from '@/components/ui/copy-button'
import { useRovingTabs } from '@/hooks/use-roving-tabs'
import { trackConnect, trackConnectLead, type ConnectLeadMethod } from '@/lib/analytics/track'
import { CONNECT_CLIENTS } from '@/lib/connect/clients'
import { ConnectHostIcon } from '@/lib/connect/source-icons'
import { toolfareSetup } from '@/lib/connect/toolfare-cli'
import { cn } from '@/lib/utils'

export type ConnectWay = ConnectLeadMethod

const WAYS: { id: ConnectWay; label: string; fit: string }[] = [
  { id: 'mcp', label: 'MCP', fit: 'For Claude, ChatGPT, Cursor and other AI apps' },
  { id: 'cli', label: 'CLI', fit: 'For terminals and agents with a shell' },
  { id: 'skill', label: 'Skill', fit: 'For agents that can install it themselves' },
]

function Snippet({ value, label, method }: { value: string; label: string; method: ConnectWay }) {
  return (
    <div className="relative rounded-xl bg-[#1A0A24] py-4 pe-12 ps-4 text-[#EDE6F7]">
      <pre className="font-mono text-[0.8rem] leading-relaxed break-words whitespace-pre-wrap">
        {value}
      </pre>
      <CopyButton
        value={value}
        onCopySuccess={() => {
          trackConnect('connect_way_selected', { way: method, control: 'copy' })
          trackConnectLead(method)
        }}
        aria-label={`Copy ${label}`}
        successMessage="Copied"
        className="absolute top-2.5 end-2.5 text-[#B798FD] hover:bg-white/10 hover:text-white"
      />
    </div>
  )
}

function Unavailable({ children }: { children: React.ReactNode }) {
  return <p className="rounded-xl bg-[#F3EEF9] p-4 text-sm text-[#2E113D]">{children}</p>
}

/** One way's instructions. Rendered alone so each can be checked on its own. */
export function ConnectWayPanel({
  way,
  origin,
  endpoint,
}: {
  way: ConnectWay
  origin: string
  endpoint: string | null
}) {
  const setup = toolfareSetup(origin, endpoint)
  const fit = WAYS.find(item => item.id === way)?.fit

  return (
    <div className="space-y-4">
      <p className="text-[0.95rem] text-[#2E113D]/65">{fit}</p>

      {way === 'mcp' && (
        <>
          {endpoint ? (
            <div data-connect-hero-endpoint>
              <Snippet value={endpoint} label="MCP URL" method="mcp" />
            </div>
          ) : (
            <Unavailable>
              The connection address could not be loaded. Reload the page to try again.
            </Unavailable>
          )}
          <p className="text-[0.95rem] leading-relaxed text-[#2E113D]/80">
            Add it to your AI app, then sign in to toolfare in your browser when the app asks.
            Each app adds a connector differently, and some need one more command to sign in.
          </p>
          <Link
            href="/connect/agents#setup"
            className="inline-block rounded text-[0.95rem] font-medium text-[#2E113D] underline decoration-[#B798FD] decoration-2 underline-offset-4 hover:decoration-[#2E113D] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2E113D]"
          >
            Setup steps for your app
          </Link>
          <ul className="flex flex-wrap gap-2 pt-1">
            {CONNECT_CLIENTS.map(client => (
              <li
                key={client.id}
                className="inline-flex items-center gap-2 rounded-full border border-[#2E113D]/10 py-1 pe-3 ps-1.5 text-[0.8rem] text-[#2E113D]"
              >
                <ConnectHostIcon hostId={client.id} onDark={false} className="size-5" />
                {client.name}
              </li>
            ))}
          </ul>
        </>
      )}

      {way === 'cli' && (
        <ol className="space-y-4">
          <li className="space-y-2">
            <p className="text-[0.95rem] font-medium text-[#2E113D]">1. Install it (Node 18 or newer)</p>
            <Snippet value={setup.install} label="CLI install commands" method="cli" />
          </li>
          <li className="space-y-2">
            <p className="text-[0.95rem] font-medium text-[#2E113D]">2. Sign in and approve in your browser</p>
            {setup.login ? (
              <Snippet value={setup.login} label="CLI login commands" method="cli" />
            ) : (
              <Unavailable>
                The sign-in command needs the connection address, which could not be loaded.
                Reload the page to try again.
              </Unavailable>
            )}
          </li>
        </ol>
      )}

      {way === 'skill' && (
        <>
          <Snippet value={setup.prompt} label="skill installation prompt" method="skill" />
          <p className="text-[0.95rem] leading-relaxed text-[#2E113D]/80">
            Paste this into your agent. It installs the toolfare skill and the CLI, then
            stops so you can approve the sign-in yourself.
          </p>
        </>
      )}
    </div>
  )
}

export function ConnectWays({ origin, endpoint }: { origin: string; endpoint: string | null }) {
  const [selected, setSelected] = useState(0)
  const tabs = useRovingTabs({
    count: WAYS.length,
    selectedIndex: selected,
    onSelect: index => {
      setSelected(index)
      trackConnect('connect_way_selected', { way: WAYS[index]?.id, control: 'tab' })
    },
  })
  const way = WAYS[selected]?.id ?? 'mcp'

  return (
    <div className="rounded-[1.75rem] border border-[#2E113D]/10 bg-white p-2 shadow-[0_1px_0_rgba(46,17,61,0.04),0_30px_60px_-30px_rgba(46,17,61,0.25)]">
      <div
        {...tabs.tablistProps}
        aria-label="Ways to connect"
        className="grid grid-cols-3 gap-1 rounded-[1.35rem] bg-[#F3EEF9] p-1"
      >
        {WAYS.map((item, index) => (
          <button
            key={item.id}
            {...tabs.tabProps(index)}
            className={cn(
              'rounded-[1.1rem] px-3 py-2.5 text-[0.95rem] font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2E113D]',
              selected === index
                ? 'bg-[#2E113D] text-white'
                : 'text-[#2E113D]/70 hover:text-[#2E113D]',
            )}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div {...tabs.panelProps} className="p-5 sm:p-6">
        <ConnectWayPanel way={way} origin={origin} endpoint={endpoint} />
      </div>
    </div>
  )
}
